import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { reportAPI } from '../services/api';
import { toast } from 'react-toastify';
import Icon from '../components/Icon';
import {
  GreetingHero, FilterBar, KpiTile, SectionCard, ActivityFeed, greetingFor
} from '../components/dashboard/DashboardKit';

export default function FinancialAnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('last_30_days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const stored = JSON.parse(localStorage.getItem('user') || '{}');
  const displayName = stored.full_name || stored.firstName || stored.username || stored.email || 'there';
  const firstName = displayName.split(' ')[0];

  const buildParams = (r, cs, ce) => {
    if (r === 'all') return { dateRange: 'custom', startDate: '2000-01-01', endDate: new Date().toISOString().slice(0, 10) };
    if (r === 'custom') return (cs && ce) ? { dateRange: 'custom', startDate: cs, endDate: ce } : { dateRange: 'last_30_days' };
    return { dateRange: r };
  };

  const load = async (r = range, cs = customStart, ce = customEnd) => {
    setLoading(true);
    try {
      const res = await reportAPI.getFinancialReport(buildParams(r, cs, ce));
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load financial report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const changeRange = (r) => { setRange(r); load(r, customStart, customEnd); };
  const applyCustom = () => {
    if (!customStart || !customEnd) return toast.error('Select both dates');
    setRange('custom'); load('custom', customStart, customEnd);
  };

  const s = data?.summary || {};
  const monthly = (data?.monthlyBreakdown || []).map((m) => ({ month: m.month, Payments: Number(m.payments || 0) }));
  const methods = (data?.paymentMethods || []).map((m, i) => ({ name: m.method, value: Number(m.amount || 0) }));
  const trends = (data?.trends || []).map((t) => ({ month: t.month, Payments: Number(t.payments || 0) }));
  const transactions = (data?.transactions || []).slice(0, 6);

  if (loading && !data) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: 64, height: 64, fontSize: 28, margin: '0 auto 24px' }} />
          <h3 style={{ color: '#64748b', fontWeight: 600 }}>Loading Financial Report…</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content admin-analytics">
      <GreetingHero greeting={greetingFor(new Date().getHours())} name={firstName}
        actions={<><button className="btn btn-primary btn-sm" onClick={() => load()}><Icon name="refresh" /> Refresh</button></>} />

      <FilterBar range={range} onRange={changeRange} customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd} setCustomEnd={setCustomEnd} onApplyCustom={applyCustom} />

      <div className="kpi-grid">
        <KpiTile label="Commissions Earned (KES)" value={Number(s.totalCommissionsEarned || 0).toLocaleString()} icon={<Icon name="dollar" />} color="#037b90" />
        <KpiTile label="Payments Made (KES)" value={Number(s.totalPaymentsMade || 0).toLocaleString()} icon={<Icon name="check" />} color="#10b981" />
        <KpiTile label="Pending (KES)" value={Number(s.pendingPayments || 0).toLocaleString()} icon={<Icon name="clock" />} color="#f59e0b" />
        <KpiTile label="Success Rate" value={`${s.paymentSuccessRate || 0}%`} icon={<Icon name="trending-up" />} color="#8b5cf6" />
      </div>

      <div className="dash-grid">
        <SectionCard title="Commission Trends" subtitle="Monthly commissions earned">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trends} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="finPay" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#037b90" stopOpacity={0.35} /><stop offset="95%" stopColor="#037b90" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Area type="monotone" dataKey="Payments" stroke="#037b90" fill="url(#finPay)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Commissions by Status" subtitle="Breakdown of payment amounts">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="Payments" fill="#3b82f6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      <div className="dash-grid">
        <SectionCard title="Recent Transactions" subtitle="Latest payment records">
          <div className="activity-feed">
            {transactions.length === 0 && <p style={{ color: '#94a3b8', padding: 12 }}>No transactions in this period.</p>}
            {transactions.map((t, i) => (
              <div key={t.id || i} className="activity-item">
                <div className="activity-title">{t.recruiterName} · KES {Number(t.amount || 0).toLocaleString()}</div>
                <div className="activity-desc">Ref: {t.reference} · {t.status}</div>
                <div className="activity-time">{t.date}</div>
              </div>
            ))}
          </div>
        </SectionCard>
        <SectionCard title="Top Payment Method" subtitle={s.topPaymentMethod || 'N/A'}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={methods.length ? methods : [{ name: 'No data', value: 1 }]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                {(methods.length ? methods : [{ name: 'No data' }]).map((e, i) => (<Cell key={i} fill={['#037b90', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'][i % 5]} />))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>
    </div>
  );
}
